import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'

import { submitAnswer } from '@/lib/api/evaluations'
import { PH_UNMASK } from '@/lib/privacyMask'
import type {
  AnswerResponse,
  ProbeAnswerPayload,
  ProbeDefinition,
  ProbeWord,
  ProbeWordResult,
  SoundCheckItem,
} from '@/types'
import { ProbeAScreen } from './screens/ProbeAScreen'
import { ProbeWordScreen } from './screens/ProbeWordScreen'
import '@/styles/placement-flow.css'

interface ProbeOrchestratorProps {
  evaluationId: string
  probe: ProbeDefinition
  learnerName: string
  onResponse: (response: AnswerResponse) => void
  onError: (err: unknown) => void
}

export function ProbeOrchestrator({
  evaluationId,
  probe,
  learnerName,
  onResponse,
  onError,
}: ProbeOrchestratorProps) {
  const [submitting, setSubmitting] = useState(false)
  const [wordResults, setWordResults] = useState<ProbeWordResult[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [doneCount, setDoneCount] = useState(0)

  useEffect(() => {
    setDoneCount(0)
  }, [evaluationId])

  useEffect(() => {
    setWordResults([])
    setCurrentIndex(0)
  }, [probe.id])

  const items: SoundCheckItem[] = probe.items ?? []
  const words: ProbeWord[] = probe.words ?? []
  const totalCount = probe.type === 'sound_check' ? items.length : words.length

  const submitProbe = async (finalResults: ProbeWordResult[]) => {
    if (submitting) return
    setSubmitting(true)

    const answer: ProbeAnswerPayload = { wordResults: finalResults }

    try {
      const response = await submitAnswer(evaluationId, {
        questionId: probe.id,
        answer,
      })
      onResponse(response)
    } catch (err) {
      onError(err)
    } finally {
      setSubmitting(false)
    }
  }

  const handleWordResult = (correct: boolean) => {
    const next: ProbeWordResult =
      probe.type === 'sound_check'
        ? { word: items[currentIndex].word, type: 'real', correct }
        : {
            word: words[currentIndex].word,
            type: words[currentIndex].type,
            correct,
          }
    const updated = [...wordResults, next]
    setWordResults(updated)
    setDoneCount((c) => c + 1)

    if (currentIndex + 1 < totalCount) {
      setCurrentIndex((i) => i + 1)
    } else {
      submitProbe(updated)
    }
  }

  if (totalCount === 0) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        Probe data missing - please reload.
      </div>
    )
  }

  return (
    <div className="flex w-full flex-col items-center" {...PH_UNMASK}>
      <div className="probe-orchestrator__header">
        {probe.type === 'sound_check' ? (
          <span className="hs-eyebrow probe-orchestrator__eyebrow probe-orchestrator__eyebrow--warmup">
            Warm-up - for the grown-up
          </span>
        ) : (
          <span className="hs-eyebrow probe-orchestrator__eyebrow">
            Reading together - just for fun
          </span>
        )}
        <DonePile count={doneCount} />
      </div>

      <div className="mt-6 flex w-full justify-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={`${probe.id}:${currentIndex}`}
            initial={{ opacity: 0, x: 60 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -60 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="flex w-full justify-center"
          >
            {probe.type === 'sound_check' && items[currentIndex] ? (
              <ProbeAScreen
                item={items[currentIndex]}
                learnerName={learnerName}
                doneCount={doneCount}
                submitting={submitting}
                onResult={handleWordResult}
              />
            ) : probe.type === 'word_reading' && words[currentIndex] ? (
              <ProbeWordScreen
                word={words[currentIndex].word}
                learnerName={learnerName}
                doneCount={doneCount}
                submitting={submitting}
                onResult={handleWordResult}
              />
            ) : (
              <div className="p-6 text-sm text-muted-foreground">
                Probe item missing.
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  )
}

function DonePile({ count }: { count: number }) {
  if (!count) return <span />
  return (
    <div className="probe-orchestrator__done-pile" aria-label={`${count} words so far`}>
      <div className="probe-orchestrator__done-cards" aria-hidden="true">
        {Array.from({ length: Math.min(count, 8) }, (_, i) => (
          <span
            key={i}
            className={`probe-orchestrator__done-card ${i % 2 ? 'is-tilt-right' : 'is-tilt-left'}`}
          />
        ))}
      </div>
      <span className="hs-eyebrow probe-orchestrator__eyebrow">{count} so far</span>
    </div>
  )
}
